import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Chip
} from '@material-ui/core';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  ArrowForwardRounded as GoIcon,
  CheckCircleRounded as ValidIcon,
  CancelRounded as InvalidIcon
} from '@material-ui/icons';

import {
  AuthContext,
  baseUrl,
  EN_US_LOCALE,
  SupportedLocale,
  normalizeLocale,
  setLocaleOverride,
  TestAuthUser,
  useDebounce
} from 'framework';
import { Line, LocaleSelect, TitleContainer } from '../components';

const ValidIconStyled = styled(ValidIcon)`
  color: ${p => p.theme.palette.success.main};
`;
const InvalidIconStyled = styled(InvalidIcon)`
  color: ${p => p.theme.palette.error.main};
`;

const Container = styled(Box)`
  display: flex;
  flex-direction: row;
  max-width: 640px;
  margin: ${p => p.theme.spacing(4)}px auto;
`;

const TestUserGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${p => p.theme.spacing(2)}px;
  max-width: 960px;
  margin: ${p => p.theme.spacing(3)}px auto 0;
`;

const TestUserCard = styled(Box)`
  border: 1px solid ${p => p.theme.palette.divider};
  border-radius: 12px;
  padding: ${p => p.theme.spacing(2)}px;
  background: ${p => p.theme.palette.background.paper};
`;

const HeaderRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${p => p.theme.spacing(2)}px;
`;

const HeaderLanguageSelect = styled(LocaleSelect)`
  min-width: 144px;
`;

const SocialSigninButton = styled(Button)`
  background: #ffffff;
  color: #3c4043;
  border: 1px solid #dadce0;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.16), 0 1px 3px rgba(60, 64, 67, 0.24);
  padding: ${p => p.theme.spacing(1.5, 2.5)};
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;

  &:hover {
    background: #f8fbff;
    box-shadow: 0 2px 4px rgba(60, 64, 67, 0.18),
      0 3px 8px rgba(60, 64, 67, 0.2);
  }

  &:focus {
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.24),
      0 2px 4px rgba(60, 64, 67, 0.18), 0 3px 8px rgba(60, 64, 67, 0.2);
  }

  &:disabled {
    background: #f7f8f8;
    color: rgba(60, 64, 67, 0.55);
    border-color: #e0e3e7;
  }

  & .MuiButton-label {
    justify-content: center;
  }

  & .MuiButton-startIcon {
    margin-left: 0;
    margin-right: ${p => p.theme.spacing(1.5)}px;
  }
`;

const GlobalProviderSigninContainer = styled(Box)`
  max-width: 640px;
  margin: ${p => p.theme.spacing(2)}px auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${p => p.theme.spacing(1)}px;
  width: 100%;
`;

const WorkspaceSigninRevealRow = styled(Box)`
  max-width: 640px;
  margin: ${p => p.theme.spacing(1)}px auto 0;
  display: flex;
  justify-content: center;
`;

const WorkspaceSigninRevealButton = styled(Button)`
  color: ${p => p.theme.palette.text.secondary};
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;
`;

const WorkspaceSigninPanel = styled(Box)`
  max-width: 640px;
  margin: ${p => p.theme.spacing(3)}px auto 0;
  padding: ${p => p.theme.spacing(3)}px;
  border: 1px solid ${p => p.theme.palette.divider};
  border-radius: 20px;
  background: linear-gradient(180deg, #fbfdff 0%, #f3f6fb 100%);
`;

const WorkspaceSigninHint = styled(Typography)`
  max-width: 480px;
  line-height: 1.5;
`;

const PasswordSigninButton = styled(Button)`
  min-width: 220px;
  white-space: nowrap;
`;

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.205c0-.639-.057-1.251-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615Z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.258c-.807.541-1.84.861-3.048.861-2.344 0-4.328-1.583-5.036-3.709H.957v2.332A9 9 0 0 0 9 18Z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.714A5.41 5.41 0 0 1 3.682 9c0-.595.102-1.173.282-1.714V4.954H.957A9 9 0 0 0 0 9c0 1.452.348 2.827.957 4.046l3.007-2.332Z"
    />
    <path
      fill="#EA4335"
      d="M9 3.577c1.321 0 2.507.455 3.44 1.348l2.581-2.581C13.463.891 11.426 0 9 0A9 9 0 0 0 .957 4.954l3.007 2.332C4.672 5.16 6.656 3.577 9 3.577Z"
    />
  </svg>
);

const MicrosoftMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#f25022" d="M1 1h7.6v7.6H1z" />
    <path fill="#7fba00" d="M9.4 1H17v7.6H9.4z" />
    <path fill="#00a4ef" d="M1 9.4h7.6V17H1z" />
    <path fill="#ffb900" d="M9.4 9.4H17V17H9.4z" />
  </svg>
);

const renderSocialButton = (
  label: string,
  onClick: ((event: React.MouseEvent<HTMLElement>) => void) | (() => void),
  icon: React.ReactNode,
  options?: {
    disabled?: boolean;
    fullWidth?: boolean;
    type?: 'button' | 'submit';
  }
) => (
  <SocialSigninButton
    disableElevation
    fullWidth={options?.fullWidth}
    onClick={onClick}
    startIcon={icon}
    disabled={options?.disabled}
    type={options?.type || 'button'}
  >
    {label}
  </SocialSigninButton>
);

export function Signin() {
  const initialOrgId =
    new URLSearchParams(window.location.search).get('orgId')?.toLowerCase() ||
    '';
  const [orgId, setOrgId] = useState('');
  const [authProvider, setAuthProvider] = useState<
    'cognito' | 'google' | 'microsoft' | 'test'
  >('cognito');
  const [testUsers, setTestUsers] = useState<TestAuthUser[]>([]);
  const [testUsersLoading, setTestUsersLoading] = useState(false);
  const [signingInUserId, setSigningInUserId] = useState<string | null>(null);
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { getTestUsers, loginWithTestUser } = useContext(AuthContext);
  const [showWorkspaceSignin, setShowWorkspaceSignin] = useState(
    !!initialOrgId
  );

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const latestCheckRequestId = useRef(0);

  const checkOrg = useCallback(
    async (o: string) => {
      latestCheckRequestId.current += 1;
      const requestId = latestCheckRequestId.current;

      // const { orgId } = state;
      if (!o || o.length <= 3) {
        if (requestId === latestCheckRequestId.current) {
          setValid(false);
          setAuthProvider('cognito');
          setTestUsers([]);
          setTestUsersLoading(false);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${baseUrl}/checkOrg/${o}`);
        if (requestId !== latestCheckRequestId.current) {
          return;
        }

        if (response.status === 404 || response.status === 403) {
          setValid(false);
          setAuthProvider('cognito');
          setTestUsers([]);
        } else {
          setValid(true);
          try {
            const wellKnownResponse = await fetch(
              `${baseUrl}/org/${o}/wellknown`
            );
            if (requestId !== latestCheckRequestId.current) {
              return;
            }
            if (wellKnownResponse.ok) {
              const wellKnown = (await wellKnownResponse.json()) as {
                authProvider?: 'cognito' | 'google' | 'microsoft' | 'test';
              };
              let nextProvider: 'cognito' | 'google' | 'microsoft' | 'test' =
                'cognito';

              if (wellKnown.authProvider === 'google') {
                nextProvider = 'google';
              } else if (wellKnown.authProvider === 'microsoft') {
                nextProvider = 'microsoft';
              } else if (wellKnown.authProvider === 'test') {
                nextProvider = 'test';
              }

              setAuthProvider(nextProvider);

              if (nextProvider === 'test') {
                setTestUsersLoading(true);
                try {
                  const availableUsers = await getTestUsers(o);
                  if (requestId === latestCheckRequestId.current) {
                    setTestUsers(availableUsers);
                  }
                } catch {
                  if (requestId === latestCheckRequestId.current) {
                    setTestUsers([]);
                  }
                } finally {
                  if (requestId === latestCheckRequestId.current) {
                    setTestUsersLoading(false);
                  }
                }
              } else {
                setTestUsers([]);
                setTestUsersLoading(false);
              }
            } else {
              setAuthProvider('cognito');
              setTestUsers([]);
            }
          } catch {
            if (requestId === latestCheckRequestId.current) {
              setAuthProvider('cognito');
              setTestUsers([]);
            }
          }
        }
      } catch {
        if (requestId === latestCheckRequestId.current) {
          setTestUsersLoading(false);
        }
      } finally {
        if (requestId === latestCheckRequestId.current) {
          setLoading(false);
        }
      }
    },
    [getTestUsers]
  );

  useEffect(() => {
    if (!initialOrgId) {
      return;
    }

    setOrgId(initialOrgId);
    checkOrg(initialOrgId);
  }, [checkOrg, initialOrgId]);

  const debouncedCheckOrg = useDebounce(checkOrg, 500);

  const navigateToOrg = (e: React.MouseEvent) => {
    e.preventDefault();
    history.push(`/${orgId}`);
  };

  const navigateWithCognito = (e: React.MouseEvent) => {
    e.preventDefault();
    history.push(`/${orgId}?authFlow=cognito`);
  };

  const platformGoogleSignin = () => {
    window.location.href = `${baseUrl}/auth/google/start`;
  };

  const platformMicrosoftSignin = () => {
    window.location.href = `${baseUrl}/auth/microsoft/start`;
  };

  const signInAsTestUser = async (userId: string) => {
    setSigningInUserId(userId);

    try {
      await loginWithTestUser(orgId, userId);
      window.location.href = `${window.location.origin}/${orgId}`;
    } finally {
      setSigningInUserId(null);
    }
  };

  const setLanguage = async (locale: SupportedLocale) => {
    setLocaleOverride(locale);
    await i18n.changeLanguage(locale);
  };

  const currentLanguage = normalizeLocale(i18n.language) || EN_US_LOCALE;

  const renderIcon = (v: boolean) =>
    v ? <ValidIconStyled /> : <InvalidIconStyled />;

  let workspaceSigninLabel = t('signInWithPassword');

  if (authProvider === 'google') {
    workspaceSigninLabel = t('signInWithGoogle');
  } else if (authProvider === 'microsoft') {
    workspaceSigninLabel = t('signInWithMicrosoft');
  }

  let workspaceSigninAction: React.ReactNode = null;

  if (authProvider !== 'test') {
    if (authProvider === 'google') {
      workspaceSigninAction = renderSocialButton(
        t('continueWithGoogle'),
        navigateToOrg,
        <GoogleMark />,
        {
          disabled: loading || !valid,
          type: 'submit'
        }
      );
    } else if (authProvider === 'microsoft') {
      workspaceSigninAction = renderSocialButton(
        t('continueWithMicrosoft'),
        navigateToOrg,
        <MicrosoftMark />,
        {
          disabled: loading || !valid,
          type: 'submit'
        }
      );
    } else {
      workspaceSigninAction = (
        <PasswordSigninButton
          disabled={loading || !valid}
          variant="contained"
          color="secondary"
          onClick={navigateWithCognito}
          endIcon={<GoIcon />}
          type="submit"
        >
          {t('signInWithPassword')}
        </PasswordSigninButton>
      );
    }
  }

  return (
    <>
      <TitleContainer>
        <HeaderRow>
          <Typography variant="h5">{t('signin')}</Typography>
          <HeaderLanguageSelect
            ariaLabel={t('language')}
            onChange={setLanguage}
            value={currentLanguage}
          />
        </HeaderRow>
      </TitleContainer>
      <Line />
      <GlobalProviderSigninContainer>
        {renderSocialButton(
          t('signInWithGoogle'),
          platformGoogleSignin,
          <GoogleMark />,
          {
            fullWidth: true
          }
        )}
        {renderSocialButton(
          t('signInWithMicrosoft'),
          platformMicrosoftSignin,
          <MicrosoftMark />,
          {
            fullWidth: true
          }
        )}
      </GlobalProviderSigninContainer>
      {!showWorkspaceSignin ? (
        <WorkspaceSigninRevealRow>
          <WorkspaceSigninRevealButton
            onClick={() => setShowWorkspaceSignin(true)}
          >
            {t('signInWithPassword')}
          </WorkspaceSigninRevealButton>
        </WorkspaceSigninRevealRow>
      ) : null}
      {showWorkspaceSignin ? (
        <>
          <WorkspaceSigninPanel>
            <Typography variant="subtitle2">{workspaceSigninLabel}</Typography>
            <Box mt={1} mb={3}>
              <WorkspaceSigninHint variant="body2">
                {t('signinOrgIdHint')}
              </WorkspaceSigninHint>
            </Box>
            <form>
              <Container mt={0} mb={0}>
                <TextField
                  label={t('orgId')}
                  value={orgId}
                  fullWidth
                  variant="outlined"
                  size="medium"
                  onChange={(event: React.ChangeEvent<{ value: string }>) => {
                    const newOrgId = event.target.value.toLocaleLowerCase();
                    setOrgId(newOrgId);
                    debouncedCheckOrg(newOrgId);
                  }}
                  InputProps={{
                    endAdornment: loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      renderIcon(valid)
                    )
                  }}
                />
                <Box mr={2} />
                {workspaceSigninAction}
              </Container>
            </form>
            {valid && authProvider === 'test' ? (
              <>
                <Container mb={2}>
                  <Typography variant="body2">
                    {t('testProviderSigninHint')}
                  </Typography>
                </Container>
                {testUsersLoading ? (
                  <Container>
                    <CircularProgress size={24} />
                  </Container>
                ) : (
                  <TestUserGrid>
                    {testUsers.map(user => (
                      <TestUserCard key={user.id}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mb={1}
                        >
                          <Typography variant="subtitle1">
                            {user.name}
                          </Typography>
                          <Chip
                            label={t(`testProviderRole${user.role}`)}
                            size="small"
                          />
                        </Box>
                        <Typography variant="body2">{user.email}</Typography>
                        <Box mt={1} mb={2}>
                          <Typography variant="caption">
                            {user.groups.length
                              ? user.groups.join(', ')
                              : t('testProviderNoGroups')}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          disabled={signingInUserId === user.id}
                          onClick={() => signInAsTestUser(user.id)}
                        >
                          {signingInUserId === user.id
                            ? t('loading')
                            : t('signInAsTestUser')}
                        </Button>
                      </TestUserCard>
                    ))}
                  </TestUserGrid>
                )}
              </>
            ) : null}
          </WorkspaceSigninPanel>
        </>
      ) : null}
    </>
  );
}
